<script>
  import { onMount } from 'svelte';
  import SavingsCard from '$components/SavingsCard/SavingsCard.svelte';
  import SavingsCardSkeletonTile from '$components/SavingsCardSkeletonTile/SavingsCardSkeletonTile.svelte';
  import GetCardTile from '$components/GetCardTile/GetCardTile.svelte';
  import TextExpander from '$components/TextExpander/TextExpander.svelte';
  import EmailCardOverpanel from '$components/EmailCardOverpanel/EmailCardOverpanel.svelte';
  import TextCardOverpanel from '$components/TextCardOverpanel/TextCardOverpanel.svelte';
  import DrugSavingsList from '$components/DrugSavingsList/DrugSavingsList.svelte';
  import Breadcrumbs from '$components/Breadcrumbs/Breadcrumbs.svelte';
  import ExtendedPharmaciesList from '$components/ExtendedPharmaciesList/ExtendedPharmaciesList.svelte';
  import BaseLink from '$components/BaseLink/BaseLink.svelte';
  import BaseHeading from '$components/BaseHeading/BaseHeading.svelte';
  import BaseMarkdown from '$components/BaseMarkdown/BaseMarkdown.svelte';
  import contentMap from '$utils/ContentMapPage';
  import handleSAASError from '$utils/handleSAASError';
  import { SPRITESHEET_URL } from '$utils/constants';
  import { StoredCard } from '$utils/local-storage';
  import { RetentionSent } from '$utils/session-storage';
  import fetchCard from '$libs/fetchCard';
  import currentLocation from '$stores/currentLocation';
  import currentCard from '$stores/card';
  let { data } = $props();

  const lang = data.lang;

  let query = data.cardQuery;
  let cardData = $state();
  let smsEndpoint = `${process.env.SAAS_SMS_ENDPOINT}`;
  let emailEndpoint = `${process.env.SAAS_EMAIL_ENDPOINT}`;
  // we need to determine whether client-side JS is active in order to know which card to show
  // if we just have a <noscript> tag below without such a check, then two cards will appear on the page
  let isScript = typeof window !== 'undefined';

  let card = $derived(cardData);

  onMount(async () => {
    // load card from store
    let cardVal = $currentCard;

    // if no card is in the store load card from localStorage
    if (!cardVal) {
      try {
        cardVal = JSON.parse(StoredCard.get());
      } catch (err) {
        console.warn(err); /* eslint-disable-line no-console */
      }
    }

    // if no card is in localStorage fetch card from SAAS
    const requests = [!cardVal ? fetchCard().catch(() => Promise.resolve()) : Promise.resolve()];

    const [cardRet, locationRet] = await Promise.all(requests);

    if (!cardVal && cardRet) {
      if (cardRet.status < 400) {
        const json = await cardRet.json();
        cardVal = json.cards[0];
        StoredCard.set(JSON.stringify(cardVal));
        RetentionSent.set('false');
      } else {
        const { id } = await cardRet.json();
        handleSAASError('Error fetching card from SAAS endpoint', id);
      }
    }

    cardData = {
      card: cardVal || { ...data.defaultCard },
      smsEndpoint,
      emailEndpoint,
    };

    if (locationRet) {
      $currentLocation = locationRet[0];
    }
  });
</script>

<Breadcrumbs path={data.path} crumbsText={data.crumbsText} />
<div class="flex flex-col items-center w-full bg-gray-mercury lg:shadow-content-well">
  <div
    class="flex relative justify-center w-full bg-blue-very-light py-6 lg:shadow-content-well-sm lg:h-auto lg:py-10 print:bg-white"
  >
    <div class="items-center w-full max-w-6xl lg:pb-4">
      <BaseHeading
        className="px-4 mb-2 text-xl font-semibold text-blue-navy lg:mb-4 md:text-2xl lg:text-3xl lg:text-left lg:text-blue-dark-navy xl:text-4x"
        headingLevel="h1"
      >
        {data.headingTextTemplate}
      </BaseHeading>
      <div class="grid items-center lg:grid-cols-2-auto">
        <div class="px-4 lg:text-left md:text-lg text-blue-navy">
          {@html data.content}
        </div>
        <div class="mx-4 min-h-60 md:min-w-106 lg:ml-auto lg:row-span-2 xl:ml-8">
          {#if isScript}
            {#if !card}
              <SavingsCardSkeletonTile {...data.savingsCard} translateUp={false} />
            {:else}
              <SavingsCard {...data.savingsCard} {...card.card} translateUp={false} />
            {/if}
          {/if}
          <noscript>
            <SavingsCard {...data.savingsCard} {...data.defaultCard} />
          </noscript>
        </div>
        <GetCardTile {...data.getCardTile} reducedPadding={true} />
      </div>
    </div>
  </div>
  {#if data.printHelpText}
    <div class="hidden print:flex justify-center w-full pt-20 bg-white -mt-32">
      <BaseMarkdown {...data.printHelpText} />
    </div>
  {/if}
</div>
{#if data.drugSavingsList}
  <div class="print:hidden bg-gray-mercury w-full py-6 lg:py-10">
    <div class="max-w-6xl mx-auto px-4">
      <DrugSavingsList {...data.drugSavingsList} />
    </div>
  </div>
{/if}

{#if data.marketingContent}
  <div class="print:hidden max-w-6xl mx-auto px-4 py-6 sm:pb-2 lg:pt-10 lg:pb-6">
    {#each data.marketingContent.imports as marketingContent (marketingContent.id)}
      {@const SvelteComponent = contentMap[marketingContent.__typename]}
      <SvelteComponent {...marketingContent} noWrapper={true} {lang} />
    {/each}
  </div>
{/if}

{#if data.faq}
  <div
    class="flex flex-col items-center py-8 lg:py-15 print:hidden"
    itemscope={true}
    itemtype="https://schema.org/FAQPage"
  >
    <div class="flex flex-col w-full max-w-4xl">
      <BaseHeading
        className="text-base leading-tight font-semibold text-blue-dark-navy mx-4 mb-8 lg:text-xl"
        headingLevel={data.faq.headingLevel}
      >
        {data.faq.headingText}
      </BaseHeading>
      <div class="mx-4 mb-9">
        {#each data.faq.questions as question (question.id)}
          <TextExpander {...question} />
        {/each}
      </div>
      <BaseLink
        url={`${lang === 'es' ? '/es' : ''}/help`}
        className="flex print:hidden flex-row items-center self-center text-xs lg:text-sm leading-normal font-semibold
        text-blue-cerulean hover-hover:hover:text-blue-darkish hover-hover:hover:underline active:text-blue-darkish active:underline"
      >
        <span class="mr-2">{data.faq.readMoreText}</span>
        <svg class="w-3 h-3 fill-current">
          <use xlink:href={`${SPRITESHEET_URL}#right-arrow`} />
        </svg>
      </BaseLink>
    </div>
  </div>
{/if}

{#if data.extendedPharmaciesList}
  <div class="print:hidden bg-gray-mercury w-full py-6 lg:py-10 shadow-content-well">
    <ExtendedPharmaciesList {...data.extendedPharmaciesList} pharmacies={data.pharmacies} />
  </div>
{/if}

{#if data.cardDisclaimer && data.cardDisclaimer.content}
  <div
    class="hidden print:block py-8 text-sm leading-tight text-gray-warm mx-auto max-w-4xl"
    data-testid="savings-card-page-disclaimer"
  >
    {@html data.cardDisclaimer.content}
  </div>
{/if}

<EmailCardOverpanel {...data.emailCardOverpanel} {...card} {query} {lang} />
<TextCardOverpanel {...data.textCardOverpanel} {...card} {query} {lang} />
